/**
 * Fullscreen image lightbox for the project gallery page (SKAN-7).
 *
 * Convenção de markup (renderizada por templates/_blocks/imageGallery.twig +
 * templates/_components/Lightbox.twig):
 *   <section data-lightbox-gallery>
 *     <li data-lightbox-item data-lightbox-index data-lightbox-src data-lightbox-caption> ... </li>
 *     <div data-lightbox-overlay> ... </div>
 *   </section>
 *
 * Reusa a convenção do Modal (toggle `is-active` + scroll lock via
 * documentElement.style.overflow) e os eventos globais `swiped-left`/`swiped-right`
 * disparados por src/components/Slideshow.js — sem editar nenhum dos dois.
 */

const setupGallery = (gallery) => {
  const overlay = gallery.querySelector('[data-lightbox-overlay]');
  const items = [...gallery.querySelectorAll('[data-lightbox-item]')];

  if (!overlay || items.length === 0 || overlay.dataset.lightboxReady) {
    return;
  }
  overlay.dataset.lightboxReady = 'true';

  // Mover o overlay para o <body> evita clipping/stacking-context vindos do
  // `overflow-hidden` (e eventuais `transform`) na <section> da galeria.
  document.body.appendChild(overlay);

  const root = document.documentElement;
  const image = overlay.querySelector('[data-lightbox-image]');
  const caption = overlay.querySelector('[data-lightbox-caption]');
  const counter = overlay.querySelector('[data-lightbox-counter]');
  const prevBtn = overlay.querySelector('[data-lightbox-prev]');
  const nextBtn = overlay.querySelector('[data-lightbox-next]');
  const closeBtn = overlay.querySelector('[data-lightbox-close]');
  const wrapper = overlay.querySelector('[data-lightbox-wrapper]');

  const slides = items.map((item) => ({
    src: item.dataset.lightboxSrc,
    caption: item.dataset.lightboxCaption || '',
  }));

  let current = 0;

  const render = () => {
    const slide = slides[current];
    image.setAttribute('src', slide.src);
    image.setAttribute('alt', slide.caption);

    caption.textContent = slide.caption;
    caption.style.display = slide.caption ? '' : 'none';

    counter.textContent = `${current + 1} / ${slides.length}`;

    prevBtn.classList.toggle('disabled', current === 0);
    nextBtn.classList.remove('disabled');
  };

  const open = (index) => {
    current = index;
    render();
    root.style.overflow = 'hidden';
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
  };

  const close = () => {
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    root.style.overflow = 'visible';
  };

  const isOpen = () => overlay.classList.contains('is-active');

  const prev = () => {
    if (current > 0) {
      current -= 1;
      render();
    }
  };

  const next = () => {
    current = current < slides.length - 1 ? current + 1 : 0;
    render();
  };

  // Abrir ao clicar numa imagem da galeria.
  items.forEach((item, index) => {
    item.addEventListener('click', () => open(index));
  });

  prevBtn.addEventListener('click', prev);
  nextBtn.addEventListener('click', next);
  closeBtn.addEventListener('click', close);

  // Clique no backdrop (fora da imagem e dos botões) fecha.
  overlay.addEventListener('click', (event) => {
    if (!wrapper.contains(event.target) && !event.target.closest('button')) {
      close();
    }
  });

  // Swipe mobile — eventos globais emitidos por Slideshow.js.
  overlay.addEventListener('swiped-left', next);
  overlay.addEventListener('swiped-right', prev);

  // Teclado (apenas com o overlay aberto).
  document.addEventListener('keydown', (event) => {
    if (!isOpen()) {
      return;
    }
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        next();
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      default:
        break;
    }
  });
};

const handleLightbox = () => {
  document.querySelectorAll('[data-lightbox-gallery]').forEach(setupGallery);
};

export default handleLightbox;
